import { Radio } from '@base-ui/react/radio'
import { Link } from '@tanstack/react-router'
import type { ColumnHelper, Row, Table } from '@tanstack/react-table'
import { ChevronRight, Factory, type LucideIcon, User, Warehouse } from 'lucide-react'
import { Fragment } from 'react'
import {
  AppSelect,
  type AppSelectGroup,
  type AppSelectOption,
  type AppSelectProps,
} from '~/components/new/app-ui/app-select'
import { Field, fieldErrorMessage } from '~/components/new/app-ui/form-fields/field'
import { AppTableData } from '~/components/new/app-ui/table/app-table'
import { AppTableFooterPagination } from '~/components/new/app-ui/table/app-table-footer/app-table-footer-pagination'
import { pageRange } from '~/components/new/app-ui/table/app-table-footer/page-range'
import { Avatar, AvatarFallback } from '~/components/ui/avatar'
import { Badge } from '~/components/ui/badge'
import { RadioGroup } from '~/components/ui/radio-group'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '~/components/ui/input-group'
import { PhoneInput } from '~/components/ui/phone-input'
import { Separator } from '~/components/ui/separator'
import { Switch } from '~/components/ui/switch'
import { TimePicker } from '~/components/ui/time-picker'
import type { AppFormApi } from '~/hooks/form-hooks'
import { cn } from '~/lib/utils'
import type { LogisticsSearch } from '~/routes/_authenticated/logistics/-search-schema'
import type { availabilityCounts } from './logistics-filter-options'
import { PLACE_COLORS } from './logistics-place-colors'
import type { SiteLabelValue } from './logistics-places'
import {
  LOGISTICS_FIELD_INPUT_CLASS,
  LOGISTICS_FIELD_LABEL_CLASS,
  LOGISTICS_FIELD_SELECT_CLASS,
  LOGISTICS_FIELD_SURFACE_CLASS,
  LOGISTICS_LIST_FILTER_CHIP,
  LOGISTICS_SELECT_CONTENT_CLASS,
  LOGISTICS_STATUS_SWITCH_CLASS,
  LOGISTICS_TABLE_CLASS,
  LOGISTICS_TABLE_WRAPPER_CLASS,
  LOGISTICS_TAG_CLASS,
} from './logistics-styles'

/**
 * Briques des écrans Logistique aux maquettes « Back-Office 2026 » (cadres, titres, sections,
 * cartes de choix), communes aux onglets qui passent aux maquettes (Camions, puis Hubs).
 */

/** Carte claire posée sur le fond beige des maquettes (en-têtes et sections). */
const CARD_CLASS = 'flex flex-col gap-4 rounded-2xl border border-black/6 bg-sidebar'

/** Cadre d'en-tête d'un onglet (liste, fiche). */
export const LogisticsPanel = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn(CARD_CLASS, 'p-6', className)}>{children}</div>
)

/**
 * Cadre d'une liste de référentiel (maquettes « Camions », « Hubs ») : l'en-tête (fil d'Ariane,
 * titre) espacé de 24 px, un filet, puis la barre d'outils et le tableau, plus serrés (16 px).
 */
export const LogisticsListPanel = ({
  header,
  children,
}: React.PropsWithChildren<{ header: React.ReactNode }>) => (
  <LogisticsPanel className="gap-6">
    {header}
    <Separator className="bg-black/6" />
    <div className="flex flex-col gap-4">{children}</div>
  </LogisticsPanel>
)

/**
 * Tableau d'une liste de référentiel, seulement une fois la liste lue : pendant le chargement,
 * « Chargement… » ; après un échec, l'erreur affichée par la liste suffit (pas de « Aucun
 * résultat. » trompeur). `isPending` et non `isLoading` : hors ligne, la requête en pause n'est pas
 * « en cours » mais reste en attente.
 */
export const LogisticsListTable = <TData,>({
  table,
  query,
  onRowClick,
  isRowClickable,
  pagination,
}: {
  table: Table<TData>
  query: { data: unknown; isPending: boolean }
  onRowClick?: (row: Row<TData>) => void
  isRowClickable?: (row: Row<TData>) => boolean
  /** Liste paginée (`useAppTable` avec `paginate`) : pied « 1–25 sur 160 collaborateurs ». */
  pagination?: LogisticsListPagination
}) => {
  // L'instance `table` de TanStack Table est stable et mutable : mémorisé par le React Compiler,
  // `<AppTableData table={table} />` ne se redessinerait pas quand les lignes changent (une liste
  // sans clic de ligne, aux props sinon identiques, garderait ses anciennes lignes).
  'use no memo'
  if (query.data === undefined) {
    return query.isPending ? <p className="text-sm text-muted-foreground">Chargement…</p> : null
  }
  return (
    <>
      <AppTableData
        table={table}
        columnSizing="only-explicit"
        tableClassName={LOGISTICS_TABLE_CLASS}
        wrapperClassName={LOGISTICS_TABLE_WRAPPER_CLASS}
        onRowClick={onRowClick}
        isRowClickable={isRowClickable}
      />
      {/* Une seule page : pas de pied, la liste reste celle des maquettes (sans pagination). */}
      {pagination !== undefined && table.getPageCount() > 1 && (
        <LogisticsListPaginationFooter table={table} {...pagination} />
      )}
    </>
  )
}

interface LogisticsListPagination {
  /** Tailles de page proposées (« Lignes par page »). */
  pageSizes: readonly number[]
  /** Total des lignes avec leur nom accordé : `(count) => pluralize(count, 'collaborateur')`. */
  countLabel: (count: number) => string
}

/** Pied d'une liste paginée : la plage affichée, puis le pied de pagination commun des tableaux. */
const LogisticsListPaginationFooter = <TData,>({
  table,
  pageSizes,
  countLabel,
}: LogisticsListPagination & { table: Table<TData> }) => {
  'use no memo'
  // Lignes filtrées, toutes pages confondues.
  const total = table.getPrePaginationRowModel().rows.length
  const { first, last } = pageRange(table.getState().pagination, total)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="text-sm text-muted-foreground">
        {first}–{last} sur {countLabel(total)}
      </span>
      <AppTableFooterPagination table={table} pageSizes={pageSizes} />
    </div>
  )
}

/** Teintes du titre d'en-tête : vert par défaut, prune pour les hubs (maquette « Hubs »). */
const TITLE_TONES = {
  primary: { title: 'text-primary-700', subtitle: 'text-primary-500' },
  prune: { title: 'text-secondary-prune-700', subtitle: 'text-secondary-prune-500' },
} as const

/** Titre d'en-tête (« Camions », « PL-01 ») et sa ligne de détail. */
export const LogisticsPanelTitle = ({
  title,
  subtitle,
  className,
  tone = 'primary',
}: {
  title: string
  subtitle: string
  className?: string
  tone?: keyof typeof TITLE_TONES
}) => (
  <div className={cn('flex min-w-0 flex-col justify-center', className)}>
    <h2
      className={cn(
        'font-brand text-[22px] leading-[26px] font-extrabold',
        TITLE_TONES[tone].title,
      )}
    >
      {title}
    </h2>
    <p className={cn('text-sm leading-4 font-semibold', TITLE_TONES[tone].subtitle)}>{subtitle}</p>
  </div>
)

/**
 * Fil d'Ariane d'un onglet (« Camions › Tous les camions › PL-01 ») : les étapes parentes ramènent
 * à la page Logistique avec `search` (onglet et filtres de la liste), la dernière (page courante)
 * est atténuée.
 */
export const LogisticsBreadcrumb = ({
  search,
  steps,
}: {
  search: LogisticsSearch
  steps: readonly string[]
}) => (
  <nav aria-label="Fil d'Ariane" className="flex items-center gap-1 text-xs leading-[14px]">
    {steps.map((step, index) => {
      const isLast = index === steps.length - 1
      return (
        // Liste fixe : l'index est une clé stable (le nom d'un camion peut valoir « Camions »).
        // oxlint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {index > 0 && <ChevronRight className="size-3.5 text-primary-500" aria-hidden="true" />}
          {isLast ? (
            <span aria-current="page" className="font-medium text-primary-300">
              {step}
            </span>
          ) : (
            <Link
              to="/logistics"
              search={search}
              className="font-semibold text-primary-500 hover:underline"
            >
              {step}
            </Link>
          )}
        </Fragment>
      )
    })}
  </nav>
)

/**
 * Teintes de la pastille : liste RH et fiche collaborateur, personnel rattaché d'un hub (pavé
 * clair), référent d'un hub (carte foncée). Maquettes « Back-Office 2026 ».
 */
const COLLABORATOR_AVATAR_TONES = {
  default: 'rounded-xl bg-primary-500',
  staff: 'rounded-lg bg-primary-300',
  referent: 'rounded-lg bg-primary-400',
} as const

/**
 * Pastille d'initiales d'un collaborateur : ligne de la liste RH (`row`) ou en-tête de sa fiche
 * (`header`) ; silhouette sans prénom ni nom lisibles.
 */
export const CollaboratorAvatar = ({
  initials,
  size,
  tone = 'default',
}: {
  initials: string | null
  size: 'row' | 'header'
  tone?: keyof typeof COLLABORATOR_AVATAR_TONES
}) => (
  <Avatar
    className={cn(
      'ring-0 after:hidden',
      COLLABORATOR_AVATAR_TONES[tone],
      size === 'row' ? 'size-[34px]' : 'size-12',
    )}
    aria-hidden="true"
  >
    <AvatarFallback
      className={cn(
        COLLABORATOR_AVATAR_TONES[tone],
        'text-sidebar',
        size === 'row'
          ? 'text-sm'
          : 'border border-black/6 font-brand text-[22px] leading-[26px] font-extrabold',
      )}
    >
      {initials ?? <User className={size === 'row' ? 'size-4' : 'size-6'} />}
    </AvatarFallback>
  </Avatar>
)

/**
 * Ligne d'un collaborateur (liste RH, référent et personnel d'un hub) : pastille, nom et détail,
 * puis `children` en fin de ligne (chevron, bouton). Fond par `className`, couleurs du texte par
 * `nameClassName` et `detailClassName`. Le détail se coupe, pas son suffixe.
 */
export const CollaboratorIdentityRow = ({
  className,
  children,
  initials,
  tone,
  name,
  nameId,
  nameClassName,
  detail,
  detailSuffix,
  detailClassName,
}: {
  initials: string | null
  tone?: keyof typeof COLLABORATOR_AVATAR_TONES
  name: string
  /** Id du nom, pour un bouton de la ligne qui le cite (`aria-describedby`). */
  nameId?: string
  nameClassName?: string
  /** Sous le nom (rôle, matricule), `null` pour rien. */
  detail: string | null
  detailSuffix?: string
  detailClassName?: string
  className?: string
  children?: React.ReactNode
}) => (
  <div className={cn('flex min-w-0 items-center gap-2.5', className)}>
    <CollaboratorAvatar initials={initials} size="row" tone={tone} />
    <span className="flex min-w-0 flex-1 flex-col gap-1 font-semibold">
      {/* Pas de `title` : donnée personnelle, relevée par Sentry (cf. `TextCell`). */}
      <span id={nameId} className={cn('truncate text-sm leading-4', nameClassName)}>
        {name}
      </span>
      {detail !== null && (
        <span className={cn('flex min-w-0 gap-1 text-xs leading-[14px]', detailClassName)}>
          <span className="truncate">{detail}</span>
          {detailSuffix !== undefined && <span className="shrink-0">{detailSuffix}</span>}
        </span>
      )}
    </span>
    {children}
  </div>
)

/** Carré d'icône des en-têtes (liste, fiche, sections). */
export const IconTile = ({
  icon: Icon,
  className,
  iconClassName,
}: {
  icon: LucideIcon
  className?: string
  iconClassName?: string
}) => (
  <span
    className={cn(
      'flex shrink-0 items-center justify-center rounded-xl bg-background-100',
      className,
    )}
  >
    <Icon className={cn('size-6 text-primary-500', iconClassName)} aria-hidden="true" />
  </span>
)

/**
 * Séparateur vertical entre les blocs d'un en-tête, ou entre deux indicateurs (`Kpi`). Décoratif :
 * masqué aux lecteurs d'écran, qui n'ont pas à annoncer un séparateur entre chaque chiffre.
 */
export const HeaderDivider = () => (
  <Separator orientation="vertical" aria-hidden="true" className="self-stretch bg-black/6" />
)

/** Écart du titre de section : 10 px sans icône (étape), 12 px en compact, 8 px sinon. */
const sectionTitleGap = (hasIcon: boolean, compact: boolean) => {
  if (!hasIcon) return 'gap-2.5'
  return compact ? 'gap-3' : 'gap-2'
}

/** Titre de section d'une fiche : icône, intitulé, puis un filet (plein ou en fin de ligne). */
export const SectionTitle = ({
  icon,
  title,
  inlineRule = false,
  compact = false,
  titleClassName,
}: {
  /** Sans icône : titre d'étape (« Scénario actif » du détail d'une tournée). */
  icon?: LucideIcon
  title: string
  /** Filet sur la même ligne que le titre (sous-section), sinon en dessous. */
  inlineRule?: boolean
  /** Icône de 16 px et titre de 16 px (cartes d'un panneau étroit, onglet Contraintes). */
  compact?: boolean
  titleClassName?: string
}) => (
  <>
    <div className={cn('flex items-center', sectionTitleGap(icon !== undefined, compact))}>
      {icon && (
        <IconTile
          icon={icon}
          className={cn('p-2', compact && 'rounded-[10px]')}
          iconClassName={cn('text-primary-600', compact && 'size-4')}
        />
      )}
      <h3
        className={cn(
          'font-brand font-semibold text-primary-600',
          compact ? 'text-base leading-[18px]' : 'text-lg leading-5',
          titleClassName,
        )}
      >
        {title}
      </h3>
      {inlineRule && <Separator className="flex-1 bg-black/6" />}
    </div>
    {!inlineRule && <Separator className="bg-black/6" />}
  </>
)

/** Carte de section d'une fiche (Identité, Capacité, Coût…). */
export const SectionCard = ({
  children,
  borderless = false,
  className,
}: React.PropsWithChildren<{
  /** Sans bordure : cartes d'un panneau (onglet Contraintes du détail d'une tournée). */
  borderless?: boolean
  className?: string
}>) => (
  <section className={cn(CARD_CLASS, 'p-4', borderless && 'border-0', className)}>
    {children}
  </section>
)

/** Teintes d'un indicateur : sur carte claire, ou sur la carte verte « Scénario actif ». */
const KPI_TONES = {
  default: { label: 'text-sage-500', value: 'text-primary-600' },
  inverse: { label: 'text-primary-300', value: 'text-primary-50' },
} as const

/** Indicateur d'une ligne de chiffres (« DISTANCE », « CHARGE UTILE ») : libellé, valeur dessous. */
export const Kpi = ({
  label,
  value,
  tone = 'default',
  className,
}: {
  label: string
  value: string
  tone?: keyof typeof KPI_TONES
  className?: string
}) => (
  <div className={cn('flex flex-col gap-1 text-xs leading-3.5 whitespace-nowrap', className)}>
    <span className={cn('font-medium', KPI_TONES[tone].label)}>{label}</span>
    <span className={cn('font-semibold', KPI_TONES[tone].value)}>{value}</span>
  </div>
)

/** Part d'une jauge, en % de sa longueur, et sa couleur. */
export interface GaugePart {
  percent: number
  className: string
}

/**
 * Jauge d'une carte de l'onglet Contraintes (charge d'un camion, heures d'un chauffeur) : libellé
 * et valeur au-dessus, valeur en rouge au-delà de la capacité ; sans part, pas de jauge.
 */
export const Gauge = ({
  label,
  value,
  over,
  parts,
}: {
  label: string
  value: string
  over: boolean
  parts: GaugePart[]
}) => {
  const filled = parts.reduce((total, part) => total + part.percent, 0)
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2.5 text-xs leading-3.5 font-medium">
        <span className="text-sage-500">{label}</span>
        <span className={over ? 'font-semibold text-destructive' : 'text-primary-700'}>
          {value}
        </span>
      </div>
      {parts.length > 0 && (
        <div className="h-2 w-full overflow-hidden rounded-[10px] bg-sage-300">
          {/* Parts accolées dans un remplissage arrondi : l'extrémité reste ronde. */}
          <div
            className="flex h-full overflow-hidden rounded-[10px]"
            style={{ width: `${filled}%` }}
          >
            {parts.map((part, index) => (
              <div
                key={index}
                className={cn('h-full', part.className)}
                style={{ width: filled > 0 ? `${(part.percent / filled) * 100}%` : 0 }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Tailles d'une plaque : en-tête du détail (12 px, vert foncé) ; `small` pour les cartes Trajet et la
 * carte camion de Contraintes (11 px, vert clair, capitales).
 */
const PLATE_TAG_SIZES = {
  default: 'text-xs leading-3.5 text-primary-600',
  small: 'text-[11px] leading-[13px] text-primary-500 uppercase',
} as const

/** Plaque d'un camion (détail d'une tournée) ; rien si la plaque est inconnue. */
export const PlateTag = ({
  plate,
  size = 'default',
  className,
}: {
  plate: string | null
  size?: keyof typeof PLATE_TAG_SIZES
  className?: string
}) =>
  plate === null ? null : (
    <span
      className={cn(
        'w-fit shrink-0 rounded-[3px] bg-primary-50 px-1.5 py-1 font-semibold whitespace-nowrap',
        PLATE_TAG_SIZES[size],
        className,
      )}
    >
      {plate}
    </span>
  )

/**
 * Carte de choix, états portés par `data-checked` / `data-disabled` (posés par Base UI sur une
 * option radio, à la main sur une carte d'affichage) : une seule définition de l'apparence.
 */
const OPTION_CARD_CLASS = cn(
  'group flex h-auto w-full items-center justify-start gap-4 rounded-xl border p-2 text-left text-sm leading-4 text-primary-700',
  'border-transparent bg-background-100 font-medium',
  'data-checked:border-primary-300 data-checked:bg-primary-100 data-checked:font-semibold',
  'data-disabled:opacity-50',
)

const OptionCardContent = ({ icon: Icon, label }: { icon: LucideIcon; label: string }) => (
  <>
    <span className="flex items-center justify-center rounded-lg bg-sidebar p-1 group-data-checked:bg-primary-200">
      <Icon className="size-5 text-primary-600" aria-hidden="true" />
    </span>
    {label}
  </>
)

/**
 * Carte d'un choix imposé ou indisponible (maquette : « Coût / km » seul possible) : un simple
 * affichage, pas un contrôle.
 */
export const OptionCard = ({
  icon,
  label,
  selected,
  disabled,
  title,
}: {
  icon: LucideIcon
  label: string
  selected: boolean
  disabled?: boolean
  title?: string
}) => (
  <div
    title={title}
    data-checked={selected ? '' : undefined}
    data-disabled={disabled ? '' : undefined}
    className={OPTION_CARD_CLASS}
  >
    <OptionCardContent icon={icon} label={label} />
  </div>
)

/**
 * Choix exclusif présenté en cartes (type de camion, type de rattachement) : un vrai groupe radio
 * (sémantique `radiogroup`, navigation aux flèches), dont chaque `OptionRadioCard` est une option.
 */
export const OptionRadioGroup = <TValue extends string>({
  label,
  value,
  onValueChange,
  disabled,
  className,
  children,
}: React.PropsWithChildren<{
  label: string
  value: TValue
  onValueChange: (value: TValue) => void
  disabled?: boolean
  className?: string
}>) => (
  <RadioGroup
    aria-label={label}
    value={value}
    onValueChange={(next) => onValueChange(next as TValue)}
    disabled={disabled}
    className={cn('flex gap-2', className)}
  >
    {children}
  </RadioGroup>
)

export const OptionRadioCard = ({
  value,
  icon,
  label,
}: {
  value: string
  icon: LucideIcon
  label: string
}) => (
  <Radio.Root
    value={value}
    className={cn(
      OPTION_CARD_CLASS,
      'cursor-pointer outline-none hover:bg-primary-50 focus-visible:ring-[3px] focus-visible:ring-ring/50',
      'data-checked:hover:bg-primary-100 data-disabled:cursor-not-allowed data-disabled:hover:bg-background-100',
    )}
  >
    <OptionCardContent icon={icon} label={label} />
  </Radio.Root>
)

/** Valeur absente d'une cellule de liste. */
export const Missing = () => <span className="text-sage-500">—</span>

/** Texte d'une cellule de liste, tronqué avec son infobulle ; `—` s'il manque. */
export const TextCell = ({
  value,
  personal = false,
}: {
  value: string | null
  personal?: boolean
}) =>
  value === null ? (
    <Missing />
  ) : (
    <span
      className="block truncate text-[13px] font-semibold text-primary-700"
      // Donnée personnelle (mail, téléphone) : pas d'infobulle `title`, attribut que Sentry relève
      // dans le fil d'Ariane de chaque clic, envoyé avec les erreurs.
      title={personal ? undefined : value}
    >
      {value}
    </span>
  )

/** Montant d'une cellule de liste, avec son unité atténuée (« 900 kg », « 1 250 m² »). */
export const Amount = ({ value, unit }: { value: string; unit: string }) => (
  <span className="flex items-center gap-1 whitespace-nowrap">
    <span className="text-[13px] font-semibold text-primary-700">{value}</span>
    <span className="text-[11px] font-medium text-sage-500">{unit}</span>
  </span>
)

/**
 * Site de départ : pastille aux teintes de lieu du simulateur (`PLACE_COLORS`), ronde (maquette
 * « Camions ») ou carrée (maquette « Hubs », atelier rattaché).
 */
export const SiteLabel = ({
  kind,
  name,
  shape = 'round',
}: SiteLabelValue & { shape?: 'round' | 'square' }) => {
  const Icon = kind === 'kitchen' ? Factory : Warehouse
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        className={cn(
          'flex shrink-0 items-center p-1 text-white',
          shape === 'round'
            ? `rounded-full border ${PLACE_COLORS[kind].ringClass}`
            : 'rounded-[4px]',
          PLACE_COLORS[kind].solidClass,
        )}
      >
        <Icon className="size-4" aria-hidden="true" />
      </span>
      {/* Deux lignes avant l'ellipse : un nom d'atelier long (« Atelier Rungis (démo logistique) »)
          reste lisible dans une colonne étroite, sans dépasser la hauteur de ligne des maquettes.
          `whitespace-normal` : la cellule de tableau interdit le retour à la ligne. */}
      <span
        className="line-clamp-2 text-[13px] leading-4 font-semibold break-words whitespace-normal text-primary-700"
        title={name}
      >
        {name}
      </span>
    </span>
  )
}

/**
 * Colonne « Ouvrir » des listes (maquettes « Camions », « Hubs ») : chevron de fin de ligne, la ligne
 * ouvre sa fiche au clic. `isOpenable` : pas de chevron sur une ligne dont la fiche est fermée.
 */
export const openRowColumn = <TData,>(
  columnHelper: ColumnHelper<TData>,
  isOpenable: (row: TData) => boolean = () => true,
) =>
  columnHelper.display({
    id: 'open',
    size: 38,
    header: () => <span className="sr-only">Ouvrir</span>,
    cell: ({ row }) =>
      isOpenable(row.original) && (
        <ChevronRight className="size-4 text-primary-500" aria-hidden="true" />
      ),
  })

/**
 * Interrupteur Actif / Inactif des maquettes (liste des hubs, fiche camion), suivi de son libellé,
 * grisé quand l'élément est inactif.
 */
export const LogisticsStatusSwitch = ({
  checked,
  className,
  ...props
}: React.ComponentProps<typeof Switch> & { checked: boolean }) => (
  <span className="flex items-center gap-2.5">
    <Switch {...props} checked={checked} className={cn(LOGISTICS_STATUS_SWITCH_CLASS, className)} />
    <span
      className={cn(
        'text-xs leading-[14px] font-semibold',
        checked ? 'text-primary-500' : 'text-sage-400',
      )}
    >
      {checked ? 'Actif' : 'Inactif'}
    </span>
  </span>
)

/** Filtre d'une liste de référentiel (maquettes « Camions », « Hubs ») : pastille beige et son menu. */
export const LogisticsListFilter = <TValue,>(
  props: Omit<AppSelectProps<TValue>, 'className' | 'contentClassName'>,
) => (
  <AppSelect
    {...props}
    className={LOGISTICS_LIST_FILTER_CHIP}
    contentClassName={LOGISTICS_SELECT_CONTENT_CLASS}
  />
)

/** « Parc Lucie » en vert, un prestataire en orange. */
export const AttachmentBadge = ({
  carrierName,
  className,
}: {
  carrierName: string | null
  className?: string
}) => (
  <Badge
    variant="outline"
    className={cn(
      LOGISTICS_TAG_CLASS,
      'border-black/6',
      carrierName === null ? 'text-primary-500' : 'text-secondary-orange-600',
      className,
    )}
  >
    {carrierName ?? 'Parc Lucie'}
  </Badge>
)

/** Pastille « Disponible » / « indisponible », avec un compteur optionnel (en-tête de liste). */
export const AvailabilityTag = ({ available, count }: { available: boolean; count?: number }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-lg border border-black/6 px-[9px] py-[5px] text-xs leading-[14px] font-semibold whitespace-nowrap',
      available
        ? 'bg-primary-50 text-primary-500'
        : 'bg-secondary-orange-100 text-secondary-orange-600',
    )}
  >
    {count !== undefined && (
      <span
        className={cn(
          'font-brand font-extrabold',
          available ? 'text-primary-600' : 'text-secondary-orange-700',
        )}
      >
        {count}
      </span>
    )}
    {available ? 'Disponible' : 'indisponible'}
  </span>
)

/**
 * Compteurs « Disponibilité : » d'un en-tête de liste (`availabilityCounts`), onglets Camions et RH.
 */
export const AvailabilityCounters = ({
  counts,
}: {
  counts: ReturnType<typeof availabilityCounts>
}) => (
  <div className="flex items-center gap-2">
    <span className="text-xs leading-[14px] font-semibold text-neutral-500">Disponibilité :</span>
    <AvailabilityTag available count={counts.available} />
    <AvailabilityTag available={false} count={counts.unavailable} />
  </div>
)

/**
 * Explique les noms masqués faute de droit de lecture : sans droit, `useCarrierReferences` et
 * `useVehicleReferences` ne lisent pas la liste, et les valeurs gardent le libellé de repli de
 * `logistics-places.ts` (`Transporteur #id`, `Hub #id`).
 */
export const HiddenReferencesNote = ({
  carriersHidden = false,
  hubsHidden = false,
}: {
  carriersHidden?: boolean
  hubsHidden?: boolean
}) => {
  const hidden = [carriersHidden && 'transporteurs', hubsHidden && 'hubs'].filter(Boolean)
  if (hidden.length === 0) return null
  return (
    <p className="text-sm text-muted-foreground">
      Noms des {hidden.join(' et des ')} masqués : droit de lecture requis.
    </p>
  )
}

/**
 * Saisie d'une fiche logistique (camion, hub) : libellé 10 px, champ beige avec unité facultative
 * à droite, erreur dessous. Purement visuelle : chaque fiche la branche sur son formulaire.
 */
export const LogisticsTextInput = ({
  id,
  label,
  value,
  error,
  onChange,
  onBlur,
  suffix,
  inputMode,
  placeholder,
  readOnly,
  className,
  action,
}: {
  id: string
  label: string
  value: string
  error: string | undefined
  onChange: (value: string) => void
  onBlur?: () => void
  /** Unité affichée à droite (« kg », « m² », « € »). */
  suffix?: string
  /** Bouton posé dans le champ, à droite (`InputGroupButton`). */
  action?: React.ReactNode
  inputMode?: 'decimal' | 'numeric'
  placeholder?: string
  readOnly: boolean
  className?: string
}) => (
  <Field
    id={id}
    label={label}
    error={error}
    className={cn('min-w-0 flex-1', className)}
    labelClassName="gap-1.5"
    labelTextClassName={LOGISTICS_FIELD_LABEL_CLASS}
  >
    <InputGroup className={cn(LOGISTICS_FIELD_SURFACE_CLASS, 'w-full')}>
      <InputGroupInput
        id={id}
        value={value}
        readOnly={readOnly}
        inputMode={inputMode}
        placeholder={placeholder}
        aria-invalid={error !== undefined}
        className={LOGISTICS_FIELD_INPUT_CLASS}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
      {suffix && (
        <InputGroupAddon align="inline-end" className="pr-3.5">
          <InputGroupText className="text-xs font-medium text-sage-500">{suffix}</InputGroupText>
        </InputGroupAddon>
      )}
      {action && (
        <InputGroupAddon align="inline-end" className="pr-1.5">
          {action}
        </InputGroupAddon>
      )}
    </InputGroup>
  </Field>
)

/**
 * Heure d'une fiche logistique (`TimePicker`) : même libellé et même surface que
 * `LogisticsTextInput`, erreur dessous. Purement visuelle ; désactivée en lecture seule.
 */
export const LogisticsTimeInput = ({
  id,
  label,
  value,
  error,
  onChange,
  readOnly,
  className,
  errorClassName,
}: {
  id: string
  label: string
  value: string
  error: string | undefined
  onChange: (value: string) => void
  readOnly: boolean
  className?: string
  errorClassName?: string
}) => (
  <Field
    id={id}
    label={label}
    error={error}
    className={cn('min-w-0 flex-1', className)}
    errorClassName={errorClassName}
    labelClassName="gap-1.5"
    labelTextClassName={LOGISTICS_FIELD_LABEL_CLASS}
  >
    <TimePicker
      id={id}
      value={value}
      onChange={onChange}
      disabled={readOnly}
      ariaInvalid={error !== undefined}
      className={cn(
        LOGISTICS_FIELD_SURFACE_CLASS,
        'px-3 text-sm font-medium text-primary-700 [&_svg]:text-primary-700',
      )}
    />
  </Field>
)

/** Classes du menu d'un sélecteur de fiche (`LOGISTICS_SECTION_MENU`). */
type LogisticsSelectMenu = Pick<
  AppSelectProps<unknown>,
  'contentClassName' | 'itemClassName' | 'groupLabelClassName'
>

/** Sélecteur d'une fiche logistique : libellé, liste, erreur dessous (purement visuel). */
export const LogisticsSelectInput = <TValue,>({
  id,
  label,
  placeholder,
  placeholderInMenu,
  options,
  groups,
  value,
  error,
  disabled,
  onChange,
  className,
  menu,
}: {
  id: string
  label: string
  /**
   * Texte du déclencheur sans valeur, et option « aucune valeur » du menu (sauf
   * `placeholderInMenu={false}`) ; absente, rien ne ramène à vide une fois une valeur choisie.
   */
  placeholder?: string
  /** `false` : valeur requise, pas d'option « aucune valeur » dans le menu. */
  placeholderInMenu?: boolean
  options?: AppSelectOption<TValue>[]
  groups?: AppSelectGroup<TValue>[]
  value: TValue | null
  error: string | undefined
  disabled: boolean
  onChange: (value: TValue | null) => void
  className?: string
  /** Menu à sections au style du DS (`LOGISTICS_SECTION_MENU`) ; sinon le menu par défaut. */
  menu?: LogisticsSelectMenu
}) => (
  <Field
    id={id}
    label={label}
    error={error}
    className={className}
    labelClassName="gap-1.5"
    labelTextClassName={LOGISTICS_FIELD_LABEL_CLASS}
  >
    <AppSelect
      id={id}
      placeholder={placeholder}
      placeholderInMenu={placeholderInMenu}
      className={LOGISTICS_FIELD_SELECT_CLASS}
      contentClassName={cn(LOGISTICS_SELECT_CONTENT_CLASS, menu?.contentClassName)}
      itemClassName={menu?.itemClassName}
      groupLabelClassName={menu?.groupLabelClassName}
      options={options}
      groups={groups}
      value={value}
      disabled={disabled}
      ariaInvalid={error !== undefined}
      onChange={onChange}
    />
  </Field>
)

/** Champs d'un formulaire de fiche dont la valeur est du texte (les nombres s'y saisissent en texte). */
export type LogisticsTextFieldName<TValues> = {
  [K in keyof TValues]: TValues[K] extends string ? K : never
}[keyof TValues] &
  string

export type LogisticsTextFieldProps<TValues> = {
  form: AppFormApi<TValues>
  name: LogisticsTextFieldName<TValues>
} & Omit<
  React.ComponentProps<typeof LogisticsTextInput>,
  'id' | 'value' | 'error' | 'onChange' | 'onBlur'
>

/**
 * Champ texte d'une fiche (`LogisticsTextInput` branché sur son formulaire), d'id
 * `<idPrefix>-<name>`.
 */
export const LogisticsTextField = <TValues,>({
  form,
  name,
  idPrefix,
  ...input
}: LogisticsTextFieldProps<TValues> & { idPrefix: string }) => (
  // `TValues` générique : TanStack Form ne relie pas `name` à sa valeur (`DeepValue`), que le type
  // `LogisticsTextFieldName` garantit être du texte.
  <form.Field name={name as never}>
    {(field) => (
      <LogisticsTextInput
        id={`${idPrefix}-${name}`}
        value={field.state.value as string}
        error={fieldErrorMessage(field.state.meta.errors)}
        onChange={(value) => field.handleChange(value as never)}
        onBlur={field.handleBlur}
        {...input}
      />
    )}
  </form.Field>
)

/** Choix d'un sélecteur pour la valeur courante (options à plat ou groupées, valeur affichée). */
export interface LogisticsSelectChoices {
  options?: AppSelectOption<string>[]
  groups?: AppSelectGroup<string>[]
  value: string | null
}

/**
 * Sélecteur d'une fiche (`LogisticsSelectInput` branché sur son formulaire), d'id
 * `<idPrefix>-<name>`. Sa valeur est du texte, `''` quand rien n'est choisi.
 */
export const LogisticsSelectField = <TValues,>({
  form,
  name,
  idPrefix,
  label,
  placeholder,
  placeholderInMenu,
  choicesOf,
  disabled,
  className,
  menu,
}: {
  form: AppFormApi<TValues>
  name: LogisticsTextFieldName<TValues>
  idPrefix: string
  label: string
  placeholder?: string
  placeholderInMenu?: boolean
  menu?: LogisticsSelectMenu
  /** Choix calculés depuis la valeur courante du champ (`''` = aucune). */
  choicesOf: (current: string) => LogisticsSelectChoices
  disabled: boolean
  className?: string
}) => (
  // Même contournement de typage que `LogisticsTextField`.
  <form.Field name={name as never}>
    {(field) => {
      const choices = choicesOf(field.state.value as string)
      return (
        <LogisticsSelectInput
          id={`${idPrefix}-${name}`}
          label={label}
          placeholder={placeholder}
          placeholderInMenu={placeholderInMenu}
          options={choices.options}
          groups={choices.groups}
          value={choices.value}
          error={fieldErrorMessage(field.state.meta.errors)}
          disabled={disabled}
          className={className}
          menu={menu}
          onChange={(value) => field.handleChange((value ?? '') as never)}
        />
      )
    }}
  </form.Field>
)

/**
 * Téléphone d'une fiche, d'id `<idPrefix>-<name>` : le `PhoneInput` du back-office (saisie
 * formatée « +33 6 24 81 55 02 », valeur E.164 comme la fiche RH) sur la surface de
 * `LogisticsTextInput`.
 */
export const LogisticsPhoneField = <TValues,>({
  form,
  name,
  idPrefix,
  label,
  readOnly,
  className,
}: {
  form: AppFormApi<TValues>
  name: LogisticsTextFieldName<TValues>
  idPrefix: string
  label: string
  readOnly: boolean
  className?: string
}) => (
  // Même contournement de typage que `LogisticsTextField`.
  <form.Field name={name as never}>
    {(field) => {
      const id = `${idPrefix}-${name}`
      const error = fieldErrorMessage(field.state.meta.errors)
      return (
        <Field
          id={id}
          label={label}
          error={error}
          className={cn('min-w-0 flex-1', className)}
          labelClassName="gap-1.5"
          labelTextClassName={LOGISTICS_FIELD_LABEL_CLASS}
        >
          <InputGroup className={cn(LOGISTICS_FIELD_SURFACE_CLASS, 'w-full')}>
            <PhoneInput
              id={id}
              value={field.state.value as string}
              readOnly={readOnly}
              aria-invalid={error !== undefined}
              inputComponent={InputGroupInput}
              numberInputProps={{ className: LOGISTICS_FIELD_INPUT_CLASS }}
              onChange={(value) => field.handleChange(value as never)}
              onBlur={field.handleBlur}
            />
          </InputGroup>
        </Field>
      )
    }}
  </form.Field>
)

/** Heure d'une fiche (`LogisticsTimeInput` branché sur son formulaire), d'id `<idPrefix>-<name>`. */
export const LogisticsTimeField = <TValues,>({
  form,
  name,
  idPrefix,
  ...input
}: {
  form: AppFormApi<TValues>
  name: LogisticsTextFieldName<TValues>
  idPrefix: string
} & Omit<
  React.ComponentProps<typeof LogisticsTimeInput>,
  'id' | 'value' | 'error' | 'onChange'
>) => (
  // Même contournement de typage que `LogisticsTextField`.
  <form.Field name={name as never}>
    {(field) => (
      <LogisticsTimeInput
        id={`${idPrefix}-${name}`}
        value={field.state.value as string}
        error={fieldErrorMessage(field.state.meta.errors)}
        // Sans `onBlur` sur le `TimePicker` : le choix d'une heure vaut visite, comme `HubTimeField`.
        onChange={(value) => {
          field.handleChange(value as never)
          field.handleBlur()
        }}
        {...input}
      />
    )}
  </form.Field>
)
